import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, Tooltip } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import clsx from 'clsx';
import { type PropsWithChildren, type ReactElement, useEffect, useRef, useState } from 'react';
import { ButtonGroup, OverlayTrigger, Popover } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { formatStrikeWithPrecision, parseStrike } from '@/App/tiles/Option/utils/strikeUtils.ts';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput';
import { SgInput } from '@/components/sg-design-system/components/input/SgInput';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppDispatch } from '@/store/hooks.ts';
import {
  selectOptionCurrency,
  selectOptionLegsProperties,
  selectOptionLegsValidation,
  selectOptionQuoteLegs,
} from '@/store/option/selectors/optionSelectors.ts';
import { optionSlice } from '@/store/option/slice.ts';
import type {
  LegId,
  OptionProductName,
  OptionSide,
  OptionStrike,
  OptionWay,
  SolvingSide,
  SolvingStrike,
} from '@/store/option/state.ts';
import type { IntlMessageId } from '@/types/intl';

type StrikeInputProps = {
  value: OptionStrike | SolvingStrike | undefined;
  onValueChange: (value: OptionStrike | SolvingStrike | null) => void;
  'data-e2e'?: string;
  legId: LegId;
  className?: string;
};

export function StrikeInput(props: StrikeInputProps) {
  const { value, onValueChange, 'data-e2e': dataE2e, legId, className } = props;
  const dispatch = useAppDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const {
    tileId,
    strikeEquivalent,
    canSolve,
    enoughDataToSolve,
    tcValidation,
    strikeQuoteData,
    solveState,
    isSolveRunning,
  } = useOptionSelector((option) => {
    const leg = selectOptionLegsProperties(option, legId);
    const strike = leg?.strike;

    const strikeData = selectOptionQuoteLegs(option, legId)?.strikeData;
    const strikeEquivalent: string | undefined =
      strike?.type === 'delta' || strike?.type === 'shortcut'
        ? strikeData?.absoluteStrikePrice?.toString()
        : strikeData?.deltaStrikePrice;

    const otherLegs = option.properties.legs.filter((leg) => leg.legId !== legId);
    const noOtherLegsAreSolving = !otherLegs.some((leg) => leg.strikeSolveState === 'solving');
    // straddle does not support solving for now, because the pricer cannot solve for multiple premiums
    const notStraddle = option.type !== 'Straddle';
    const canSolve = noOtherLegsAreSolving && notStraddle;

    // solving popup only opens once every other field it needs (strike/expiry/notional of other legs) is filled in
    const enoughDataToSolve =
      otherLegs.every((leg) => isDefined(leg.strike) && isDefined(leg.expiryDate) && isDefined(leg.notional)) &&
      isDefined(leg?.expiryDate) &&
      isDefined(leg?.notional);

    const tcValidation = selectOptionLegsValidation(option, legId)?.strike ?? [];

    return {
      tileId: option.properties.tileId,
      solveState: leg?.strikeSolveState,
      strikeQuoteData: strikeData,
      strikeEquivalent,
      canSolve,
      enoughDataToSolve,
      tcValidation,
      isSolveRunning: option.properties.rfs.status === 'AWAITING' && strike?.type === 'solving',
    };
  });

  const solving = solveState === 'solving';
  const [showSolvingPopup, setShowSolvingPopup] = useState(false);

  // bumped to force SgInput to remount and discard stale "?" text on rejection
  const [inputResetKey, setInputResetKey] = useState(0);

  function handleChange(changeValue: string) {
    if (changeValue.trim() === '?') {
      // clear this leg's strike whether or not we can actually solve it (another leg may already be solving)
      onValueChange(null);

      if (canSolve) {
        dispatch(optionSlice.actions.setSolveState({ tileId, legId, solveState: 'solving' }));
      } else {
        setInputResetKey((key) => key + 1);
      }

      return;
    }

    // setSolving(false);
    const value = parseStrike(changeValue);
    // TODO: if strike is not valid => call TC to get errors and display them in the UI
    //  OR: make the input red
    if (isDefined(value)) {
      onValueChange(value);
      dispatch(optionSlice.actions.setSolveState({ tileId, legId, solveState: 'notSolved' }));
      return;
    }

    // user cleared the "?" they typed to request solving: leave solving mode
    if (changeValue.trim() === '' && solving) {
      dispatch(optionSlice.actions.setSolveState({ tileId, legId, solveState: 'notSolved' }));
      setShowSolvingPopup(false);
    }
  }

  // opens the popup as soon as enough data is available, immediately or once it's filled in later
  useEffect(() => {
    if (solving && enoughDataToSolve) {
      setShowSolvingPopup(true);
    }
  }, [solving, enoughDataToSolve]);

  const solvedStrike = strikeQuoteData?.solvedStrike;

  useEffect(() => {
    if (!isSolveRunning && isDefined(solvedStrike)) {
      setShowSolvingPopup(false);
    }
  }, [isSolveRunning, solvedStrike]);

  // drive "?" through the controlled value so SgInput's dirty-tracking notices when it's cleared
  const formattedValue =
    value?.type === 'solving'
      ? (strikeQuoteData?.solvedStrike ?? '?')
      : solving
        ? '?'
        : formatStrikeWithPrecision(value);

  function handleSolve(way: OptionWay, solvingSide: SolvingSide, solveValue: number) {
    sgwtWebAnalytics?.trackEvent('Option', 'Solve', `${way} ${solveValue} on ${solvingSideToString(solvingSide)} `);
    onValueChange({ type: 'solving', solveValue, solvingSide, way });
  }

  const { warnDeltaEquivalent, positive } = computeDeltaEquivalentWarning(strikeEquivalent);

  return (
    <SolvingPopup
      legId={legId}
      onSolve={handleSolve}
      show={showSolvingPopup}
      onToggle={setShowSolvingPopup}
      loading={isSolveRunning}
      enoughDataToSolve={enoughDataToSolve}
    >
      <TradeCaptureValidationWrapper validation={tcValidation}>
        <EquivalentDeltaWarning warnDeltaEquivalent={warnDeltaEquivalent} positive={positive}>
          <SgInput
            key={inputResetKey}
            value={formattedValue ?? undefined}
            onChange={handleChange}
            valuePosition="start"
            size="sm"
            className={clsx(className, {
              'bg-info bg-opacity-20': solving,
            })}
            data-e2e={dataE2e}
            placeholder="Strike"
            unitPosition="end"
            selectAllOnFocus
            unit={strikeEquivalent}
            unitIcon={solving ? 'auto_fix_high' : undefined}
            onUnitClick={() => {
              if (solving && enoughDataToSolve) {
                setShowSolvingPopup(true);
              }
            }}
          />
        </EquivalentDeltaWarning>
      </TradeCaptureValidationWrapper>
    </SolvingPopup>
  );
}
function EquivalentDeltaWarning({
  warnDeltaEquivalent,
  positive,
  children,
}: DeltaEquivalentWarning & { children: ReactElement }) {
  if (warnDeltaEquivalent) {
    return (
      <div className="position-relative invalid-warning">
        <Tooltip
          initialOpen
          contents={<FormattedMessage id="option.strike.tooltip.50d" values={{ positive }} />}
          position="right"
          color="warning"
        >
          <div>{children}</div>
        </Tooltip>
      </div>
    );
  }
  return children;
}

interface DeltaEquivalentWarning {
  warnDeltaEquivalent: boolean;
  positive: boolean;
}

function computeDeltaEquivalentWarning(strikeEquivalent: string | undefined): DeltaEquivalentWarning {
  const deltaEquivalent = parseDecimal(strikeEquivalent?.toLowerCase().replace('d', ''));
  const warnDeltaEquivalent = Math.abs(deltaEquivalent) > 50;
  const positive = deltaEquivalent > 0;

  return {
    warnDeltaEquivalent,
    positive,
  };
}

interface PopupProps {
  legId: LegId;
  onSolve: (way: OptionWay, side: SolvingSide, solveValue: number) => void;
  show: boolean;
  onToggle?: (nextShow: boolean) => void;
  loading?: boolean;
  enoughDataToSolve: boolean;
}

function SolvingPopup({
  legId,
  children,
  onSolve,
  show,
  onToggle,
  loading,
  enoughDataToSolve,
}: PropsWithChildren<PopupProps>) {
  const [value, setValue] = useState<number | undefined>();
  const premiumInputRef = useRef<HTMLInputElement>(null);
  const solveButtonRef = useRef<HTMLButtonElement>(null);
  // owed a focus on "Solve" once it's enabled (a disabled button can't receive focus)
  const [pendingSolveFocus, setPendingSolveFocus] = useState(false);
  const {
    currentUser: { userType },
  } = useUserContext();
  const isInternalSales = userType === 'Internal Sales';
  const { optionProductName, multileg, isCompactView, premiumCurrency, premiumCurrencyCode, legWay } =
    useOptionSelector((option) => {
      const currentLeg = selectOptionLegsProperties(option, legId);

      const premiumCurrency = option.properties.premiumType.premiumCurrency;
      const { currency1, currency2 } = selectOptionCurrency(option);
      const premiumCurrencyCode = premiumCurrency === 1 ? currency1 : currency2;

      const legWay: OptionWay = currentLeg?.side === 'Sell' ? 'Bid' : 'Ask';
      return {
        optionProductName: option.type,
        multileg: option.properties.legs.length > 1,
        isCompactView: option.properties.uiState.strategyPanelMode === 'hidden',
        premiumCurrency,
        premiumCurrencyCode,
        legWay,
      };
    });
  const [way, setWay] = useState<OptionWay>(legWay);
  // Only in RiskReversal, in compact view
  const [solveSide, setSolveSide] = useState<'BuyCall' | 'SellCall'>('BuyCall');

  // give focus to (and select the content of) the premium field every time the popup opens,
  // whether it just opened from typing "?" or was reopened via the magic wand icon
  useEffect(() => {
    if (show) {
      premiumInputRef.current?.focus();
    }
  }, [show]);

  const modalTitleId = getModalTile(optionProductName, multileg);

  function handleSolve() {
    let solvingSide: SolvingSide = { type: 'strategy' };
    if (isCompactView) {
      solvingSide =
        optionProductName === 'RiskReversal'
          ? { type: 'riskReversal', solveSide }
          : { type: 'compactView', overrideSolveSide: getSideFromWay(way) };
    }

    if (isDefined(value)) {
      onSolve(way, solvingSide, value);
    }
  }

  const disableSolve = !enoughDataToSolve || isNotDefined(value) || loading === true;

  // "Solve" only becomes enabled after the value commit re-renders, so defer the focus until then
  useEffect(() => {
    if (pendingSolveFocus && !disableSolve) {
      solveButtonRef.current?.focus();
      setPendingSolveFocus(false);
    }
  }, [pendingSolveFocus, disableSolve]);

  return (
    <OverlayTrigger
      placement="right"
      show={show}
      onToggle={onToggle}
      rootClose
      trigger={[]}
      overlay={
        <Popover className="p-2">
          <Popover.Header>
            <FormattedMessage tagName={'h5'} id={modalTitleId} />
          </Popover.Header>

          <Popover.Body>
            <div className="d-flex flex-column gap-3">
              {optionProductName === 'RiskReversal' && isCompactView && (
                <ButtonGroup size="sm" className="w-100">
                  <Button
                    className={clsx({ active: solveSide === 'SellCall' })}
                    onClick={() => setSolveSide('SellCall')}
                  >
                    <FormattedMessage id={'option.solving.RiskReversal.Sell'} />
                  </Button>
                  <Button className={clsx({ active: solveSide === 'BuyCall' })} onClick={() => setSolveSide('BuyCall')}>
                    <FormattedMessage id={'option.solving.RiskReversal.Buy'} />
                  </Button>
                </ButtonGroup>
              )}

              {isCompactView ? (
                <ButtonGroup size="sm" className="w-100">
                  <Button className={clsx({ active: way === 'Bid' })} onClick={() => setWay('Bid')}>
                    <FormattedMessage id={'option.solving.Bid'} />
                  </Button>
                  <Button className={clsx({ active: way === 'Ask' })} onClick={() => setWay('Ask')}>
                    <FormattedMessage id={'option.solving.Ask'} />
                  </Button>
                </ButtonGroup>
              ) : (
                <FormattedMessage
                  id={legWay === 'Bid' ? 'option.way.buy' : 'option.way.pay'}
                  values={{ isInternalSales }}
                  tagName={'h6'}
                />
              )}

              <div
                onKeyDown={(event) => {
                  // Tab goes straight to "Solve": stopPropagation is needed because AutoAdvanceForm
                  // (wrapping the whole tile) would otherwise hijack Tab/Enter and steal the focus back
                  const hasTypedValue = (premiumInputRef.current?.value ?? '').trim() !== '';

                  if (event.key === 'Tab' && !event.shiftKey && hasTypedValue) {
                    event.preventDefault();
                    event.stopPropagation();
                    setPendingSolveFocus(true);
                    premiumInputRef.current?.blur(); // commits the value; SgNumberInput only does so on blur
                  }
                }}
              >
                <SgAmountInput
                  ref={premiumInputRef}
                  data-e2e="premiumModal"
                  size="sm"
                  value={value}
                  onChange={setValue}
                  unit={premiumCurrencyCode}
                  unitBackgroundColor={premiumCurrency === 2 ? 'warning' : undefined}
                />
              </div>

              <div className={'d-flex gap-2 justify-content-end mb-2'}>
                <Button data-e2e="premiumModalCancel-button" onClick={() => onToggle?.(false)}>
                  <FormattedMessage id={'option.solving.cancel'} />
                </Button>
                <Button
                  ref={solveButtonRef}
                  loading={loading}
                  disabled={disableSolve}
                  data-e2e="premiumModalSubmit-button"
                  purpose="primary"
                  onClick={handleSolve}
                  onKeyDown={(event) => {
                    // same AutoAdvanceForm hijack as above, on Enter this time
                    if (event.key === 'Enter') {
                      event.stopPropagation();
                    }
                  }}
                >
                  <FormattedMessage id={'option.solving.solve'} />
                </Button>
              </div>
            </div>
          </Popover.Body>
        </Popover>
      }
    >
      <div className="d-flex flex-grow-1">{children}</div>
    </OverlayTrigger>
  );
}

function getModalTile(optionProductName: OptionProductName, multileg: boolean): IntlMessageId {
  switch (optionProductName) {
    case 'Vanilla':
      return multileg ? 'option.solving.Strategy.title' : 'option.solving.Vanilla.title';
    default:
      return `option.solving.${optionProductName}.title`;
  }
}

function parseDecimal(value: string | undefined, defaultValue = 0): number {
  if (isNotDefined(value)) {
    return defaultValue;
  }
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? defaultValue : parsed;
}

function getSideFromWay(way: OptionWay): OptionSide {
  return way === 'Ask' ? 'Buy' : 'Sell';
}

function solvingSideToString(solvingSide: SolvingSide) {
  switch (solvingSide.type) {
    case 'strategy':
      return 'strategy';
    case 'compactView':
      return `compactView ${solvingSide.overrideSolveSide}`;
    case 'riskReversal':
      return `riskReversal ${solvingSide.solveSide}`;
  }
}
